import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { config } from './config';
import { clearDevice, openAppDb } from './db/schema';
import { createRuntime } from './runtime';
import { FakeAuthProvider } from './auth/fake';
import { SupportSessionAuthProvider, readSupportSession } from './auth/support';
import type { AuthProvider } from './auth/provider';
import { App } from './ui/App';
import './styles.css';

/**
 * Boot.
 *
 * The order matters in one place: IndexedDB opens before the first render, so
 * the first paint is already the mirror rather than an empty screen that fills
 * in. On an installed PWA that is the difference between opening the book and
 * watching it load.
 *
 * The service worker precaches the shell and **nothing else**. There is no
 * runtime caching of `/v1` and there must not be: the app reads its own mirror,
 * and an HTTP cache in front of `core-api` would be a *second* offline store
 * with its own staleness that the status strip could not see and §8.1's "the UI
 * says plainly how stale it is" could not describe. One counter, one book — and
 * one cache.
 */

async function boot(): Promise<void> {
  // Firebase is imported dynamically: it is the largest thing in the bundle by
  // some distance, and a development build never touches it. One chunk that
  // only the real sign-in pays for.
  // The console's "see what she sees" look arrives as a token in the URL
  // fragment (`auth/support.ts`). Read first and scrubbed at once, before any
  // other provider is constructed: a tab opened by the console is that look
  // and nothing else, whatever staff sign-in this browser may also hold.
  const supportToken = readSupportSession({
    location: window.location,
    history: window.history,
    storage: safeSessionStorage(),
  });

  const auth: AuthProvider =
    supportToken !== null
      ? new SupportSessionAuthProvider({ token: supportToken })
      : config.auth.mode === 'fake'
        ? new FakeAuthProvider({ tenantId: config.auth.tenantId })
        : new (await import('./auth/firebase')).FirebaseAuthProvider(config.auth.firebase);

  const db = await openAppDb();
  // A support look starts from an empty mirror, so what the operator sees is
  // this tenant's book hydrated under this seat — never a page left behind by
  // whoever used this browser last. `signOut` clears it again at the end.
  if (supportToken !== null) await clearDevice(db);
  const runtime = createRuntime({
    db,
    auth,
    baseUrl: config.apiBaseUrl,
    reportingBaseUrl: config.reportingApiBaseUrl,
    pastDays: config.mirrorPastDays,
    futureDays: config.mirrorFutureDays,
    chunkDays: config.calendarChunkDays,
  });

  const container = document.getElementById('root');
  if (container === null) throw new Error('no #root');
  createRoot(container).render(
    <StrictMode>
      <App runtime={runtime} />
    </StrictMode>,
  );

  registerServiceWorker();
}

/**
 * Register the generated worker, by hand.
 *
 * It is registered **after** the first render, not before: a service worker
 * install competing with the first paint is a slower start for no benefit, and
 * the app is fully usable — offline included — before the worker exists at all,
 * because the mirror is IndexedDB rather than an HTTP cache.
 *
 * `registerType: 'autoUpdate'` plus `skipWaiting` means a new build takes over
 * on the next load. On a counter iPad that is left open for weeks, waiting for
 * every tab to close would mean shipping a fix nobody receives.
 */
function registerServiceWorker(): void {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch(() => {
    // No worker means no offline *shell*. The book still works for as long as
    // the tab lives, so this is a degradation, not a failure to start.
  });
}

/** `sessionStorage` can throw in a locked-down context; a support look then lives in memory only. */
function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

void boot();
